import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Send, X, User, Building2, MessageCircle } from "lucide-react";
import { useAuthState } from "@/hooks/useAuthState";
import { useMessages } from "@/hooks/useMessages";
import { Conversation, Message } from "@/integrations/mysql/types";
import { ConversationWithDetails } from "@/services/messageService";
import { useToast } from "@/hooks/use-toast";
import { sanitizeText, isValidText } from "@/lib/sanitize";
import { getCoverImageUrl } from "@/lib/imageMap";
import { UserPhotoService } from "@/services/userPhotoService";
import { messageService } from "@/services/messageService";
import { UserService } from "@/services/userService";

interface ChatProps {
  conversationId: string;
  onClose: () => void;
}

const Chat = ({ conversationId, onClose }: ChatProps) => {
  const [conversation, setConversation] = useState<ConversationWithDetails | null>(null);
  const [newMessage, setNewMessage] = useState("");
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { userId, userType, userPhoto } = useAuthState();
  const { toast } = useToast();
  const { 
    conversations, 
    currentMessages, 
    loading, 
    error, 
    loadMessages, 
    sendMessage 
  } = useMessages();
  
  // Estado para foto do outro usuário
  const [otherUserPhoto, setOtherUserPhoto] = useState<string | null>(null);

  // Função para buscar foto do outro usuário
  const fetchOtherUserPhoto = async (otherUserId: number) => {
    try {
      const user = await UserService.getUserById(otherUserId);
      setOtherUserPhoto(user?.profile_photo || null);
    } catch (error) {
      console.error('Erro ao buscar foto do outro usuário:', error);
      setOtherUserPhoto(null);
    }
  };

  useEffect(() => {
    if (conversationId && userId) {
      loadConversation();
    }
  }, [conversationId, userId]);

  useEffect(() => {
    // Scroll para a última mensagem quando as mensagens carregarem
    if (currentMessages.length > 0) {
      setTimeout(() => {
        scrollToBottom();
      }, 100); // Pequeno delay para garantir que o DOM foi atualizado
    }
  }, [currentMessages]);

  useEffect(() => {
    // Scroll para a última mensagem quando a conversa for carregada
    if (conversation && currentMessages.length > 0) {
      setTimeout(() => {
        scrollToBottom();
      }, 200); // Delay um pouco maior para garantir que tudo foi renderizado
    }
  }, [conversation]);

  const loadConversation = async () => {
    try {
      const fromList = conversations.find(
        (c) => String(c.id) === String(conversationId),
      );
      const foundConversation =
        fromList ??
        (await messageService.getConversationById(parseInt(conversationId, 10)));

      setConversation(foundConversation);

      const otherUserId =
        userType === 'guest'
          ? foundConversation.owner_id
          : foundConversation.guest_id;

      if (otherUserId && otherUserId !== userId) {
        await fetchOtherUserPhoto(otherUserId);
      }

      await loadMessages(foundConversation.id);
    } catch (error) {
      console.error('Erro ao carregar conversa:', error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar a conversa.",
        variant: "destructive",
      });
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !conversation || !userId) return;

    // Validar e sanitizar a mensagem
    const trimmedMessage = newMessage.trim();
    
    if (!isValidText(trimmedMessage)) {
      toast({
        title: "Mensagem inválida",
        description: "A mensagem contém conteúdo não permitido.",
        variant: "destructive",
      });
      return;
    }

    try {
      setSending(true);
      
      const message = await sendMessage(conversation.id, trimmedMessage);

      if (message) {
        setNewMessage("");
        
        // Atualizar conversa
        setConversation(prev => prev ? {
          ...prev,
          updated_at: new Date().toISOString(),
          last_message: message
        } : null);

        // Scroll para a nova mensagem
        setTimeout(() => {
          scrollToBottom();
        }, 100);
      }

    } catch (error) {
      console.error("Error sending message:", error);
      toast({
        title: "Erro",
        description: "Não foi possível enviar a mensagem.",
        variant: "destructive",
      });
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ 
        behavior: "smooth",
        block: "end",
        inline: "nearest"
      });
    }
  };

  const getOtherUser = () => {
    if (!conversation || !userId) return null;
    
    if (userType === 'guest') {
      return {
        name: conversation.owner_name || 'Proprietário',
        email: '',
        phone: ''
      };
    } else {
      return {
        name: conversation.guest_name || 'Hóspede',
        email: '',
        phone: ''
      };
    }
  };

  const formatTime = (dateString: string | Date) => {
    const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
    
    // Converter para horário de Brasília (UTC-3)
    const brasiliaDate = new Date(date.toLocaleString("en-US", {timeZone: "America/Sao_Paulo"}));
    const now = new Date(new Date().toLocaleString("en-US", {timeZone: "America/Sao_Paulo"}));
    
    const isToday = brasiliaDate.toDateString() === now.toDateString();
    const isYesterday = brasiliaDate.toDateString() === new Date(now.getTime() - 24 * 60 * 60 * 1000).toDateString();
    
    if (isToday) {
      return brasiliaDate.toLocaleTimeString('pt-BR', { 
        hour: '2-digit', 
        minute: '2-digit',
        timeZone: 'America/Sao_Paulo'
      });
    } else if (isYesterday) {
      return `Ontem ${brasiliaDate.toLocaleTimeString('pt-BR', { 
        hour: '2-digit', 
        minute: '2-digit',
        timeZone: 'America/Sao_Paulo'
      })}`;
    } else {
      return brasiliaDate.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
        timeZone: 'America/Sao_Paulo'
      }) + ' ' + brasiliaDate.toLocaleTimeString('pt-BR', { 
        hour: '2-digit', 
        minute: '2-digit',
        timeZone: 'America/Sao_Paulo'
      });
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <Card className="w-full max-w-2xl h-[600px]">
          <CardContent className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600 dark:text-gray-400">Carregando conversa...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!conversation) {
    console.log('❌ Conversa não carregada. Estado atual:', { conversation, conversationId, loading });
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <Card className="w-full max-w-2xl h-[600px]">
          <CardContent className="flex items-center justify-center h-full">
            <div className="text-center">
              <p className="text-gray-600 dark:text-gray-400">
                {loading ? 'Carregando conversa...' : 'Conversa não encontrada.'}
              </p>
              <Button onClick={onClose} className="mt-4">
                Fechar
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const otherUser = getOtherUser();

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-2xl h-[600px] flex flex-col">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4 border-b flex-shrink-0">
          <div className="flex items-center gap-3">
            <Avatar className="h-10 w-10">
              <AvatarImage src={getCoverImageUrl(conversation)} />
              <AvatarFallback>
                <Building2 className="h-5 w-5" />
              </AvatarFallback>
            </Avatar>
            <div>
              <CardTitle className="text-lg">{conversation.property_title}</CardTitle>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {otherUser?.name}
              </p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>

        <CardContent className="flex-1 flex flex-col p-0 min-h-0">
          {/* Messages Area */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 min-h-0 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 dark:scrollbar-thumb-gray-600 dark:scrollbar-track-gray-800">
            {currentMessages.length === 0 ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <MessageCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500 dark:text-gray-400">
                    Nenhuma mensagem ainda. Inicie a conversa!
                  </p>
                </div>
              </div>
            ) : (
              currentMessages.map((message) => {
                const isOwnMessage = message.sender_id === userId;
                const senderName = isOwnMessage ? "Você" : (conversation?.guest_id === message.sender_id ? "Hóspede" : "Proprietário");
                
                // Determinar o ID do outro usuário para a foto
                const otherUserId = userType === 'guest' ? conversation?.owner_id : conversation?.guest_id;
                
                
                return (
                  <div
                    key={message.id}
                    className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`flex gap-2 max-w-[80%] ${isOwnMessage ? 'flex-row-reverse' : 'flex-row'}`}>
                      <Avatar className="h-8 w-8 flex-shrink-0">
                        <AvatarImage 
                          src={isOwnMessage 
                            ? (userPhoto && userId ? UserPhotoService.getUserPhotoUrl(userId, userPhoto) : undefined)
                            : (otherUserPhoto && otherUserId ? UserPhotoService.getUserPhotoUrl(otherUserId, otherUserPhoto) : undefined)
                          } 
                        />
                        <AvatarFallback className={isOwnMessage ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}>
                          <User className="h-4 w-4" />
                        </AvatarFallback>
                      </Avatar>
                      <div className={`flex flex-col ${isOwnMessage ? 'items-end' : 'items-start'}`}>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                            {senderName}
                          </span>
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {formatTime(message.created_at)}
                          </span>
                        </div>
                        <div
                          className={`px-3 py-2 rounded-lg ${
                            isOwnMessage
                              ? 'bg-blue-600 text-white'
                              : 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100'
                          }`}
                        >
                          <p 
                            className="text-sm break-words"
                            dangerouslySetInnerHTML={{
                              __html: sanitizeText(message.message, {
                                maxLength: 1000,
                                allowHtml: false,
                                allowLineBreaks: true
                              })
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div className="border-t p-4 flex-shrink-0">
            <div className="flex gap-2">
              <Input
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Digite sua mensagem..."
                disabled={sending}
                className="flex-1"
              />
              <Button 
                onClick={handleSendMessage} 
                disabled={!newMessage.trim() || sending}
                size="icon"
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Chat;